import axios from "axios";

/* Django API + JWT storage shared by the login page and admin pages. */
export const API_URL = process.env.REACT_APP_DJANGO_URL || "https://mockq.wahylab.com";
export const TOKEN_KEYS = { access: "access_token", refresh: "refresh_token" };

// Where non-admin users go after login. Change to your real student landing route.
export const STUDENT_HOME = "/home";
export const ADMIN_HOME = "/admin/dashboard";
export const LOGIN_PATH = "/login";

const AUTH_EVENT = "auth:changed";

export const getAccessToken = () => localStorage.getItem(TOKEN_KEYS.access);
export const getRefreshToken = () => localStorage.getItem(TOKEN_KEYS.refresh);
export const setAccessToken = (token) => localStorage.setItem(TOKEN_KEYS.access, token);

/** Call right after a successful /api/auth/login/ response. */
export function saveSession({ access, refresh }) {
  localStorage.setItem(TOKEN_KEYS.access, access);
  localStorage.setItem(TOKEN_KEYS.refresh, refresh);
  window.dispatchEvent(new Event(AUTH_EVENT));
}

export function clearAuth() {
  localStorage.removeItem(TOKEN_KEYS.access);
  localStorage.removeItem(TOKEN_KEYS.refresh);
  window.dispatchEvent(new Event(AUTH_EVENT));
}

export const onAuthChange = (handler) => {
  window.addEventListener(AUTH_EVENT, handler);
  return () => window.removeEventListener(AUTH_EVENT, handler);
};

export const isAdminUser = (user) => !!user && (user.is_staff === true || user.role === "admin");
export const homePathFor = (user) => (isAdminUser(user) ? ADMIN_HOME : STUDENT_HOME);

/* Axios instance with Bearer token + automatic refresh on 401 */
export const djangoApi = axios.create({ baseURL: API_URL });

djangoApi.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let refreshPromise = null;

djangoApi.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const refresh = getRefreshToken();

    if (error.response?.status === 401 && original && !original._retry && refresh) {
      original._retry = true;
      try {
        refreshPromise =
          refreshPromise ||
          axios
            .post(`${API_URL}/api/auth/token/refresh/`, { refresh })
            .then((r) => r.data.access)
            .finally(() => {
              refreshPromise = null;
            });
        const newAccess = await refreshPromise;
        setAccessToken(newAccess);
        original.headers.Authorization = `Bearer ${newAccess}`;
        return djangoApi(original);
      } catch (e) {
        clearAuth();
        window.location.assign(LOGIN_PATH);
        return Promise.reject(e);
      }
    }
    return Promise.reject(error);
  }
);

/* Auth endpoints (all already exist in Django) */
export const loginRequest = (email, password) =>
  axios.post(`${API_URL}/api/auth/login/`, { email, password });

export const getProfile = () => djangoApi.get("/api/auth/profile/");

export const changePassword = (currentPassword, newPassword) =>
  djangoApi.post("/api/auth/change-password/", {
    current_password: currentPassword,
    new_password: newPassword,
  });

/** Pulls a readable message out of a DRF error response. */
export function apiErrorMessage(err, fallback = "Something went wrong. Please try again.") {
  const d = err?.response?.data;
  if (!d) return err?.message === "Network Error" ? "Cannot reach the server." : fallback;
  if (typeof d === "string") return d;
  if (d.detail) return d.detail;
  if (d.message) return d.message;
  const first = Object.values(d)[0];
  return Array.isArray(first) ? first[0] : typeof first === "string" ? first : fallback;
}